/**
 * Product Gifts Functionality
 * 处理产品赠品的自动加购和交互逻辑
 */

class ProductGifts {
  constructor() {
    this.giftItems = document.querySelectorAll('.product-gifts__item');
    this.isProcessing = false;
    this.cartUpdateUnsubscriber = null;
    this.lastProcessTime = 0;
    this.init();
  }

  init() {
    if (this.giftItems.length === 0) {
      console.log('🎁 No gift items found');
      return;
    }

    console.log(`🎁 Product Gifts initialized with ${this.giftItems.length} gift(s)`);
    this.bindEvents();
    this.checkGiftAvailability();
  }

  bindEvents() {
    // 使用主题的发布订阅系统监听购物车更新
    if (typeof subscribe === 'function') {
      this.cartUpdateUnsubscriber = subscribe('cart-update', (event) => {
        // 只有当更新来源不是产品赠品时才处理，避免循环触发
        if (!event || event.source !== 'product-gifts') {
          this.handleMainProductAdded(event);
        } else {
          console.log('🎁 Skipping cart-update from product-gifts to avoid loop');
        }
      });
      console.log('🎁 Successfully subscribed to cart-update events');
    } else {
      console.warn('🎁 Subscribe function not available, using fallback method');
      this.bindProductFormEvents();
    }
    
    // 监听购物车更新事件
    document.addEventListener('cart:updated', (event) => {
      this.handleCartUpdated(event);
    });

    // 为赠品项添加点击事件（可选功能）
    this.giftItems.forEach(item => {
      item.addEventListener('click', (event) => {
        this.handleGiftItemClick(event, item);
      });
    });
  }

  bindProductFormEvents() {
    // 备用方法：直接监听表单提交
    const productForms = document.querySelectorAll('product-form form');
    
    productForms.forEach(form => {
      form.addEventListener('submit', (event) => {
        // 存储表单数据
        const formData = new FormData(form);
        this.mainProductData = {
          id: formData.get('id'),
          quantity: formData.get('quantity') || 1
        };
        
        // 延迟处理赠品
        setTimeout(async () => {
          await this.handleMainProductAdded();
        }, 2000);
      });
    });
  }



  async handleMainProductAdded(event) {
    if (this.isProcessing) return;
    
    // 防止短时间内重复触发（3秒内只处理一次）
    const currentTime = Date.now();
    if (currentTime - this.lastProcessTime < 3000) {
      console.log('🎁 Skipping duplicate request within 3 seconds');
      return;
    }
    this.lastProcessTime = currentTime;
    
    console.log('🎁 Main product added to cart, checking for auto-add gifts...', event);
    
    // 如果有事件数据，存储主产品信息
    if (event && event.detail) {
      this.mainProductData = {
        id: event.detail.productVariantId,
        quantity: 1
      };
    }
    
    const autoAddGifts = Array.from(this.giftItems).filter(item => 
      item.dataset.autoAdd === 'true'
    );

    if (autoAddGifts.length === 0) {
      console.log('🎁 No auto-add gifts found');
      return;
    }

    console.log(`🎁 Found ${autoAddGifts.length} auto-add gifts`);

    // 延迟一点时间确保主产品已经成功添加
    setTimeout(async () => {
      // 检查购物车中是否已经有赠品，避免重复添加
      const existingGifts = await this.checkExistingGifts();
      
      for (const giftItem of autoAddGifts) {
        const giftProductId = giftItem.dataset.giftProductId;
        
        // 如果购物车中已经有这个赠品，跳过
        if (existingGifts.includes(giftProductId)) {
          console.log(`🎁 Gift ${giftProductId} already in cart, skipping...`);
          giftItem.classList.add('gift-added');
          continue;
        }
        
        await this.addGiftToCart(giftItem);
      }
    }, 500);
  }



  async addGiftToCart(giftItem) {
    if (this.isProcessing) return;
    
    this.isProcessing = true;
    const giftProductId = giftItem.dataset.giftProductId;
    
    if (!giftProductId) {
      console.error('🎁 Gift product ID not found');
      this.isProcessing = false;
      return;
    }

    try {
      // 添加加载状态
      giftItem.classList.add('gift-loading');
      
      console.log(`🎁 Adding gift product ${giftProductId} to cart...`);
      
      // 构建请求数据
      const requestData = {
        id: giftProductId,
        quantity: 1,
        properties: {
          '_gift_product': 'true',
          '_gift_with_main_product': this.mainProductData?.id || 'unknown'
        }
      };

      // 发送添加到购物车的请求
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('🎁 Gift added successfully:', result);
        
        // 更新UI状态
        giftItem.classList.remove('gift-loading');
        giftItem.classList.add('gift-added');
        
        // 立即更新购物车计数
        this.updateCartCount();
        
        // 延迟触发完整的购物车更新
        setTimeout(() => {
          this.triggerCartUpdate();
        }, 200);
        
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
    } catch (error) {
      console.error('🎁 Error adding gift to cart:', error);
      
      // 更新UI状态
      giftItem.classList.remove('gift-loading');
      giftItem.classList.add('gift-error');
      
      // 静默处理错误，不显示提示消息
      console.error('🎁 Gift addition failed, but continuing silently');
      
    } finally {
      this.isProcessing = false;
    }
  }

  async checkExistingGifts() {
    // 检查购物车中已有的赠品
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        
        // 查找所有标记为赠品的商品
        const existingGifts = cart.items
          .filter(item => item.properties && item.properties._gift_product === 'true')
          .map(item => item.variant_id.toString());
        
        console.log('🎁 Existing gifts in cart:', existingGifts);
        return existingGifts;
      }
    } catch (error) {
      console.warn('🎁 Could not check existing gifts:', error);
    }
    
    return [];
  }

  async checkGiftAvailability() {
    // 检查赠品产品的可用性
    for (const giftItem of this.giftItems) {
      const giftProductHandle = giftItem.dataset.giftProductHandle;
      
      if (!giftProductHandle) continue;
      
      try {
        const response = await fetch(`/products/${giftProductHandle}.js`);
        if (response.ok) {
          const product = await response.json();
          
          // 检查产品是否可用
          if (!product.available) {
            giftItem.classList.add('gift-unavailable');
            giftItem.setAttribute('title', '该赠品当前不可用');
          }
          
          // 存储产品信息
          giftItem.dataset.giftProductAvailable = product.available;
          
        }
      } catch (error) {
        console.warn(`🎁 Could not check availability for gift ${giftProductHandle}:`, error);
      }
    }
  }

  handleGiftItemClick(event, giftItem) {
    // 可选：点击赠品项时的行为
    const giftProductHandle = giftItem.dataset.giftProductHandle;
    
    if (giftProductHandle && event.ctrlKey) {
      // Ctrl+点击时打开产品页面
      window.open(`/products/${giftProductHandle}`, '_blank');
    }
  }

  // 移除了showGiftMessage方法，现在静默处理

  triggerCartUpdate() {
    // 立即更新购物车计数
    this.updateCartCount();
    
    // 使用主题的发布订阅系统触发购物车更新
    if (typeof publish === 'function') {
      publish('cart-update', {
        source: 'product-gifts',
        cartData: null
      });
    }
    
    // 备用方案：直接更新购物车UI
    this.updateCartUI();
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        
        // 更新购物车计数显示
        const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count < 100 ? cart.item_count : '99+';
          if (el.hasAttribute('data-cart-count')) {
            el.setAttribute('data-cart-count', cart.item_count);
          }
        });
        
        console.log(`🎁 Cart count updated to: ${cart.item_count}`);
        return cart.item_count;
      }
    } catch (error) {
      console.warn('🎁 Could not update cart count:', error);
    }
  }

  async updateCartUI() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();
        
        // 更新购物车计数显示
        const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]');
        cartCountElements.forEach(el => {
          el.textContent = cart.item_count < 100 ? cart.item_count : '99+';
          if (el.hasAttribute('data-cart-count')) {
            el.setAttribute('data-cart-count', cart.item_count);
          }
        });
        
        // 更新购物车总价显示
        const cartTotalElements = document.querySelectorAll('[data-cart-total]');
        cartTotalElements.forEach(el => {
          el.textContent = this.formatPrice(cart.total_price);
        });
        
        // 更新购物车抽屉内容
        await this.updateCartDrawer();
        
        return cart;
      }
    } catch (error) {
      console.warn('🎁 Could not update cart UI:', error);
    }
  }

  async updateCartDrawer() {
    try {
      // 获取购物车section的HTML
      const response = await fetch('/?section_id=minicart-form');
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // 更新购物车抽屉内容
        const cartDrawer = document.querySelector('#minicart-form');
        const newCartContent = doc.querySelector('#minicart-form');
        
        if (cartDrawer && newCartContent) {
          cartDrawer.innerHTML = newCartContent.innerHTML;
          
          // 重新初始化购物车功能
          if (typeof BlsLazyloadImg !== 'undefined') {
            BlsLazyloadImg.init();
          }
          
          console.log('🎁 Cart drawer updated successfully');
        }
      }
    } catch (error) {
      console.warn('🎁 Could not update cart drawer:', error);
    }
  }

  formatPrice(price) {
    // 格式化价格显示
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 2
    }).format(price / 100);
  }

  handleCartUpdated(event) {
    // 处理购物车更新事件
    console.log('🎁 Cart updated:', event.detail);
    
    // 如果需要，可以在这里添加额外的逻辑
    // 例如：检查购物车中是否已有赠品，避免重复添加
  }

  // 公共方法：手动添加赠品
  async addGiftManually(giftProductId) {
    const giftItem = document.querySelector(`[data-gift-product-id="${giftProductId}"]`);
    if (giftItem) {
      await this.addGiftToCart(giftItem);
    }
  }

  // 公共方法：移除赠品
  async removeGift(giftProductId) {
    try {
      // 获取购物车
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // 查找赠品项
      const giftItem = cart.items.find(item => 
        item.product_id.toString() === giftProductId.toString() && 
        item.properties && 
        item.properties._gift_product === 'true'
      );
      
      if (giftItem) {
        // 移除赠品项
        const removeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: giftItem.key,
            quantity: 0
          })
        });
        
        if (removeResponse.ok) {
          console.log('🎁 Gift removed from cart');
          this.triggerCartUpdate();
        }
      }
    } catch (error) {
      console.error('🎁 Error removing gift:', error);
    }
  }

  /**
   * 清理事件监听器
   */
  cleanup() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = null;
    }
    console.log('🎁 Product Gifts cleanup completed');
  }
}

// 简化的样式 - 只保留必要的加载和状态样式
const messageStyles = `
  .product-gifts__item.gift-loading {
    opacity: 0.7;
    pointer-events: none;
  }
  
  .product-gifts__item.gift-loading::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 20px;
    height: 20px;
    margin: -10px 0 0 -10px;
    border: 2px solid rgba(var(--color-foreground), 0.3);
    border-top: 2px solid rgb(var(--color-foreground));
    border-radius: 50%;
    animation: spin 1s linear infinite;
    z-index: 10;
  }
  
  .product-gifts__item.gift-unavailable {
    opacity: 0.5;
    pointer-events: none;
  }
  
  .product-gifts__item.gift-unavailable::before {
    content: "不可用";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(239, 68, 68, 0.9);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 10px;
    z-index: 10;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 添加样式到页面
const styleSheet = document.createElement('style');
styleSheet.textContent = messageStyles;
document.head.appendChild(styleSheet);

// 初始化产品赠品功能
function initProductGifts() {
  // 等待全局函数加载完成
  if (typeof subscribe === 'function' || document.querySelector('product-form')) {
    window.productGifts = new ProductGifts();
  } else {
    // 如果还没有加载完成，等待一段时间再尝试
    setTimeout(initProductGifts, 100);
  }
}

// 如果页面已经加载完成，立即初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductGifts);
} else {
  initProductGifts();
}

// 导出类供其他脚本使用
window.ProductGifts = ProductGifts; 